/* eslint-disable class-methods-use-this */
import { AcceptJs, AcceptJsCardData } from "@shared/lib/authorize.net/types";
import { loadAuthorize } from "@shared/lib/authorize.net/loader";

import { DataProvider } from "@shared/constants/common";
import { recordAppError } from "@shared/utils/errors/errorLogger";
import UserBasedTicketBasketRepository from "@app-contexts/user/features/basket/data/repositories/UserBasedTicketBasketRepository/UserBasedTicketBasketRepository";

import BasketRepositoryFactory from "@shared/modules/basket/factory/BasketRepositoryFactory";
import VenueBasedTicketBasketRepository from "@app-contexts/dashboard/features/box-office/basket/data/repositories/VenueBasedTicketBasketRepository";
import { PAYMENT_MANAGER_CONFIRMATION_STRATEGY } from "@shared/modules/payments/utils/manager/utils";
import { extractErrorMessage } from "@shared/utils/errors/error-utils";

import PaymentGateway from "../PaymentGateway";

import { PaymentServiceBasket } from "../../services/useBasketPaymentService";
import AuthorizeElements from "./AuthorizeElements";
import { PurchaseOptsType } from "../../types";

import { PaymentConfigurationError } from "../../utils/errors/PaymentConfigurationError";
import { PaymentInitializationError } from "../../utils/errors/PaymentInitializationError";
import { PaymentProcessingError } from "../../utils/errors/PaymentProcessingError";
import { isPaymentOrchestrationError } from "../../utils/errorUtils";
import { createPaymentLogger } from "../../utils/payment-logger";
import {
  buildBasePurchaseData,
  resolveAddresses,
} from "../../utils/purchase/build-purchase-data";

const log = createPaymentLogger("AuthorizePaymentGateway");

class AuthorizePaymentGateway extends PaymentGateway {
  public static gatewayName = "AuthorizePaymentGateway";

  instance: AcceptJs | null = null;
  elementsInstance: AuthorizeElements | undefined = undefined;
  initializationPromise: Promise<AcceptJs | null> | undefined = undefined;
  clientKey: string | undefined = undefined;
  apiLoginID: string | undefined = undefined;
  confirmationStrategy: "card" | undefined = undefined;

  /**
   * Initializes this AuthorizePaymentGateway by keeping track of the clientKey and apiLoginID which are used during the checkout process.
   */
  async initialize(basket: PaymentServiceBasket) {
    log.info("Initializing");

    if (!this.initializationPromise) {
      this.initializationPromise = loadAuthorize();
    }

    // Create elementsInstance eagerly so AuthorizeElement components can register
    // their DOM refs while the Accept.js script is still loading asynchronously.
    if (!this.elementsInstance) {
      this.elementsInstance = new AuthorizeElements();
    }

    if (!basket?.paymentGateway) {
      throw new PaymentConfigurationError(
        "Tried to initialize an AuthorizePaymentGateway with a basket that has no paymentGateway",
        {
          metadata: {
            gatewayName: AuthorizePaymentGateway.gatewayName,
            operation: "initialize",
            missingField: "paymentGateway",
          },
        }
      );
    }

    if (
      !basket.paymentGateway.api_login_id ||
      !basket.paymentGateway.publishable_api_key
    ) {
      throw new PaymentConfigurationError(
        "Tried to initialize an AuthorizePaymentGateway with a misconfigured paymentGateway",
        {
          metadata: {
            gatewayName: AuthorizePaymentGateway.gatewayName,
            operation: "initialize",
            missingCredentials: {
              apiLoginId: !basket.paymentGateway.api_login_id,
              publishableApiKey: !basket.paymentGateway.publishable_api_key,
            },
          },
        }
      );
    }

    this.clientKey = basket.paymentGateway.publishable_api_key;
    this.apiLoginID = basket.paymentGateway.api_login_id;

    try {
      this.instance = await this.initializationPromise;
    } catch (error) {
      // Clear the failed promise to allow retries on subsequent calls
      this.initializationPromise = undefined;
      this.instance = null;
      this.elementsInstance = undefined;

      log.error("Initialization failed, cleared for retry", {
        errorMessage: extractErrorMessage(error),
      });

      if (isPaymentOrchestrationError(error)) {
        throw error;
      }

      throw new PaymentInitializationError(
        `AcceptJS initialization failed: ${extractErrorMessage(error)}`,
        {
          metadata: {
            gatewayName: AuthorizePaymentGateway.gatewayName,
            operation: "initialize",
            originalError: extractErrorMessage(error),
          },
        }
      );
    }

    return this;
  }

  public async updatePaymentObject() {
    // Unused, as Authorize does not have objects that have lifecycles.
    log.info("Skipping updatePaymentObject — no lifecycle objects");

    return this;
  }

  /**
   * Begin a confirmation flow
   */
  public async confirmPayment(
    confirmationStrategy: PAYMENT_MANAGER_CONFIRMATION_STRATEGY,
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    if (!this.instance) {
      throw new PaymentInitializationError(
        "Tried to initialize AuthorizePaymentGateway but AcceptJS library is not available",
        {
          metadata: {
            gatewayName: AuthorizePaymentGateway.gatewayName,
            operation: "confirmPayment",
          },
        }
      );
    }

    log.info(`Confirming payment: ${confirmationStrategy}`);

    switch (confirmationStrategy) {
      case PAYMENT_MANAGER_CONFIRMATION_STRATEGY.MANUAL_CARD_ENTRY: {
        return this.confirmCardPayment(basketType, basket, purchaseOpts);
      }
      // default to card if somehow no strategy could be handled
      default: {
        return this.confirmCardPayment(basketType, basket, purchaseOpts);
      }
    }
  }

  /**
   * Confirm a card payment using a client secret.
   * Creates a payment token using the card information collected via elements
   * rendered through the elementsInstance
   */
  private async confirmCardPayment(
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    if (!this.instance) {
      throw new PaymentInitializationError(
        "Tried to create payment token but AcceptJS is not available",
        {
          metadata: {
            gatewayName: AuthorizePaymentGateway.gatewayName,
            operation: "confirmCardPayment",
          },
        }
      );
    }

    const token = await this.createPaymentToken(basket, purchaseOpts);

    log.info("Created payment token");

    if (!token.token) {
      throw new PaymentProcessingError(
        "Authorize.Net token generation failed",
        {
          metadata: {
            gatewayName: AuthorizePaymentGateway.gatewayName,
            operation: "confirmCardPayment",
            tokenGenerated: Boolean(token.token),
          },
          userMessage:
            "Failed to generate secure payment token from Authorize.Net. Please check your card information and try again.",
        }
      );
    }

    return this.purchase(basketType, basket, token.token, purchaseOpts);
  }

  /**
   * Creates a token to use for a purchase using the user input on the CardElement
   * and any other necessary elements
   */
  private async createPaymentToken(
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    if (!this.instance || !this.elementsInstance) {
      throw new PaymentInitializationError(
        "Tried to create payment token but AuthorizePaymentGateway is not initialized",
        {
          metadata: {
            gatewayName: AuthorizePaymentGateway.gatewayName,
            operation: "createPaymentToken",
            hasInstance: Boolean(this.instance),
            hasElementsInstance: Boolean(this.elementsInstance),
          },
        }
      );
    }

    const name = purchaseOpts.name_on_card;
    const cardNumberEl = this.elementsInstance.getElement("cardNumber");
    const cardExpiryEl = this.elementsInstance.getElement("cardExpiry");
    const cardCvcEl = this.elementsInstance.getElement("cardCvc");

    if (!cardNumberEl) {
      throw new PaymentProcessingError(
        "Tried to create payment token but card number element not found in form",
        {
          metadata: {
            gatewayName: AuthorizePaymentGateway.gatewayName,
            operation: "createPaymentToken",
            missingElement: "cardNumber",
          },
          userMessage:
            "Card number field is not available. Please refresh the page and try again.",
        }
      );
    }

    return new Promise<{ token: string }>((resolve, reject) => {
      if (!this.instance) {
        throw new PaymentInitializationError(
          "Tried to create payment token but AcceptJS instance is not available",
          {
            metadata: {
              gatewayName: AuthorizePaymentGateway.gatewayName,
              operation: "createPaymentToken",
            },
          }
        );
      }

      if (
        !basket.paymentGateway ||
        !basket.paymentGateway.publishable_api_key ||
        !basket.paymentGateway.api_login_id
      ) {
        throw new PaymentProcessingError(
          "Tried to create payment token but payment gateway credentials are missing",
          {
            metadata: {
              gatewayName: AuthorizePaymentGateway.gatewayName,
              operation: "createPaymentToken",
              hasPaymentGateway: Boolean(basket.paymentGateway),
              hasPublishableApiKey: Boolean(
                basket.paymentGateway?.publishable_api_key
              ),
              hasApiLoginId: Boolean(basket.paymentGateway?.api_login_id),
            },
            userMessage:
              "Payment gateway credentials are missing or invalid for token creation. Please contact support.",
          }
        );
      }

      // Remove formatting from card number
      const cardNumber = cardNumberEl.value.replaceAll(" ", "");

      // Deconstruct card expiry field to fit the shape that Authorize requires
      const cardExpiry = cardExpiryEl.value.replaceAll("/", "");
      const cardExpMonth = cardExpiry
        .substring(0, cardExpiry.length / 2)
        .trim();
      const cardExpYear = cardExpiry.substring(cardExpiry.length / 2).trim();

      const cardData: AcceptJsCardData = {
        cardNumber,
        month: cardExpMonth,
        year: cardExpYear,
        cardCode: cardCvcEl.value,
      };

      // Name and Postal are optional but could be assumed it aids in a successful confirmation
      if (name) {
        cardData.fullName = name;
      }

      const { billingAddress } = resolveAddresses(basket, purchaseOpts);
      if (billingAddress && "postal" in billingAddress) {
        cardData.zip = billingAddress.postal as string;
      }

      this.instance.dispatchData(
        {
          authData: {
            clientKey: basket.paymentGateway.publishable_api_key,
            apiLoginID: basket.paymentGateway.api_login_id,
          },
          cardData,
        },
        (response) => {
          if (response.messages.resultCode === "Error") {
            const authorizeErrors = response.messages.message.map((m) => ({
              code: m.code,
              text: m.text,
            }));

            // Record each individual error to Sentry for per-message visibility
            // eslint-disable-next-line no-restricted-syntax
            for (const err of authorizeErrors) {
              recordAppError(new Error(`${err.code}:${err.text}`));
            }

            const firstError = authorizeErrors[0];

            reject(
              new PaymentProcessingError(
                `Authorize.Net tokenization failed: ${firstError?.code}:${firstError?.text}`,
                {
                  metadata: {
                    gatewayName: AuthorizePaymentGateway.gatewayName,
                    operation: "createPaymentToken",
                    authorizeErrorCode: firstError?.code,
                    authorizeErrors,
                    resultCode: response.messages.resultCode,
                  },
                  userMessage:
                    firstError?.text ||
                    "Please check your card details and try again.",
                }
              )
            );
          } else {
            resolve({
              token: response.opaqueData.dataValue,
            });
          }
        }
      );
    });
  }

  /**
   * Performs a `/purchase` call to the basket, essentially purchasing the basket via Charges API.
   * This should only be used for venues that have PaymentIntents *disabled*. When this flow is followed, it means that the purchase
   * flow will not follow an ASYNCHRONOUS_CONFIRMATION and ASYNCHRONOUS_CAPTURE flow.
   * In other words, we will not be handling long-running client-side flows like 3DS,
   * and we won't have to wait for a payment capture result.
   *
   * For more information about ASYNCHRONOUS_CONFIRMATION and ASYNCHRONOUS_CAPTURE,
   * @see {@link PaymentHandler}
   */
  // eslint-disable-next-line class-methods-use-this
  private async purchase(
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    token?: string,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    log.info("Purchasing");

    if (!basket.basketId) {
      throw new PaymentProcessingError(
        "Tried to confirm payment but basket ID is missing",
        {
          metadata: {
            gatewayName: AuthorizePaymentGateway.gatewayName,
            operation: "purchase",
          },
          userMessage:
            "Cannot process payment because the basket ID is missing. Please refresh the page and try again.",
        }
      );
    }

    const repository:
      | UserBasedTicketBasketRepository
      | VenueBasedTicketBasketRepository
      | undefined = BasketRepositoryFactory.getRepository(
      basketType,
      basket.paymentVenue
    );

    if (!repository) {
      throw new PaymentProcessingError(
        "Tried to confirm payment but cannot determine payment repository",
        {
          metadata: {
            gatewayName: AuthorizePaymentGateway.gatewayName,
            operation: "purchase",
            basketType,
            paymentVenue: basket.paymentVenue,
          },
          userMessage:
            "Cannot process payment because the appropriate payment repository could not be determined. This may be due to invalid basket type configuration.",
        }
      );
    }

    const purchaseData = buildBasePurchaseData(
      basketType,
      basket,
      purchaseOpts
    );

    // If there is a token passed in, set this as the credit_card data
    if (token) {
      purchaseData.credit_card = {
        charge_type: "ct_token",
        token_id: token,
      };
    }

    const { billingAddress, shippingAddress } = resolveAddresses(
      basket,
      purchaseOpts
    );

    if (billingAddress) {
      purchaseData.billing_address = billingAddress;
    }

    if (shippingAddress) {
      purchaseData.shipping_address = shippingAddress;
    }

    return repository
      .purchase(basket.basketId, purchaseData, {
        showToastOnError: false, // Should be handled by PaymentManager
      })
      .catch((err) => {
        return this.handleConfirmError(err);
      });
  }

  async postProcessCheckout() {
    log.info("Checking for invoice");

    /* eslint-disable no-await-in-loop -- https://eslint.org/docs/latest/rules/no-await-in-loop#when-not-to-use-it */
    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    if (!this.basketPaymentService || !this.invoicePaymentService) {
      throw new PaymentProcessingError(
        "Tried to complete checkout but required payment services are not available",
        {
          metadata: {
            gatewayName: AuthorizePaymentGateway.gatewayName,
            operation: "postProcessCheckout",
            hasBasketPaymentService: Boolean(this.basketPaymentService),
            hasInvoicePaymentService: Boolean(this.invoicePaymentService),
          },
          userMessage:
            "Cannot complete payment processing because required payment services are not available.",
        }
      );
    }

    // First, poll for the basket until we get a valid invoice_id
    const MAX_BASKET_ATTEMPTS = 5;
    let finalBasket;
    let invoiceId = null;

    for (let attempts = 1; attempts <= MAX_BASKET_ATTEMPTS; attempts++) {
      finalBasket = await this.basketPaymentService.refresh({
        no_lock: true,
      });

      if (finalBasket?.id == null) {
        throw new PaymentProcessingError(
          "Tried to post-process checkout but cannot retrieve basket information",
          {
            metadata: {
              gatewayName: AuthorizePaymentGateway.gatewayName,
              operation: "postProcessCheckout",
              attempts,
              maxAttempts: MAX_BASKET_ATTEMPTS,
            },
            userMessage:
              "Unable to retrieve basket information after payment processing. Please check your order status or contact support.",
          }
        );
      }

      // If we have an invoice ID, break out of the polling loop
      if (finalBasket.invoice_id != null) {
        invoiceId = finalBasket.invoice_id;
        break;
      }

      // Wait before trying again if we haven't found an invoice_id yet
      if (attempts < MAX_BASKET_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    }

    // If we couldn't get an invoice_id after all attempts, throw an error
    if (invoiceId == null) {
      throw new PaymentProcessingError(
        "Tried to post-process checkout but invoice generation failed",
        {
          metadata: {
            gatewayName: AuthorizePaymentGateway.gatewayName,
            operation: "postProcessCheckout",
            maxAttempts: MAX_BASKET_ATTEMPTS,
            finalBasketId: finalBasket?.id,
          },
          userMessage:
            "Payment was processed but invoice could not be generated after maximum attempts. Please contact support to confirm your order status.",
        }
      );
    }

    if (finalBasket == null) {
      throw new PaymentProcessingError(
        "Tried to post-process checkout but cannot retrieve final basket",
        {
          metadata: {
            gatewayName: AuthorizePaymentGateway.gatewayName,
            operation: "postProcessCheckout",
            invoiceId,
          },
          userMessage:
            "Unable to retrieve final basket information after payment processing. Please contact support to confirm your order.",
        }
      );
    }

    return finalBasket;
  }
}

export default AuthorizePaymentGateway;
